/**
 * Who a check-in cycle counts — for HR to sign off before launch (HR UAT list #20-#22).
 *
 * The headcount, everyone the year-end ring-fence leaves out (with the title
 * that did it) and everyone Too Soon To Rate (hired less than three months
 * before the check-in end date). Worked out live from the cycle, so moving its
 * end date moves the list. The progress reports count the same people. People
 * who have left but whose account is still active are a count of their own (#109).
 */
import React, { useEffect, useState } from 'react'
import { Loader2, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { adminService } from '@/services/api/adminService'
import type { CyclePopulation, CyclePopulationPerson } from '@/services/api/adminService'

interface Props {
  cycleId: number | null
  onClose: () => void
}

const day = (iso?: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

function csv(pop: CyclePopulation): string {
  const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const rows: unknown[][] = [['Why left out', 'Employee ID', 'Name', 'Job title', 'Function', 'Campus', 'First hire']]
  pop.ring_fenced.forEach((p) => rows.push([`Ring-fenced: ${p.reason}`, p.employee_id, p.name, p.job_title, p.function, p.campus, '']))
  pop.too_soon_to_rate.forEach((p) => rows.push(['Too Soon To Rate', p.employee_id, p.name, p.job_title, p.function, p.campus, p.first_hire]))
  return rows.map((r) => r.map(cell).join(',')).join('\r\n')
}

function download(pop: CyclePopulation) {
  const blob = new Blob([csv(pop)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${pop.name.replace(/[^\w-]+/g, '_')}_left_out.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const PeopleTable: React.FC<{ people: CyclePopulationPerson[]; last: 'reason' | 'first_hire' }> = ({ people, last }) => (
  <div className="max-h-64 overflow-y-auto rounded-md border">
    <table className="w-full text-left text-xs">
      <thead className="sticky top-0 bg-gray-50 text-gray-600">
        <tr>
          <th className="px-2 py-1.5">Name</th>
          <th className="px-2 py-1.5">Employee ID</th>
          <th className="px-2 py-1.5">Job title</th>
          <th className="px-2 py-1.5">Function</th>
          <th className="px-2 py-1.5">{last === 'reason' ? 'Why' : 'First hire'}</th>
        </tr>
      </thead>
      <tbody>
        {people.map((p) => (
          <tr key={p.employee_id} className="border-t">
            <td className="px-2 py-1.5">{p.name}</td>
            <td className="px-2 py-1.5 font-mono">{p.employee_id}</td>
            <td className="px-2 py-1.5">{p.job_title}</td>
            <td className="px-2 py-1.5">{p.function || '—'}</td>
            <td className="px-2 py-1.5">{last === 'reason' ? p.reason : day(p.first_hire)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const CyclePopulationDialog: React.FC<Props> = ({ cycleId, onClose }) => {
  const [pop, setPop] = useState<CyclePopulation | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (cycleId == null) return
    let live = true
    setPop(null)
    setError(null)
    adminService.getCyclePopulation(cycleId)
      .then((p) => { if (live) setPop(p) })
      .catch(() => { if (live) setError("The list couldn't be loaded. Please try again.") })
    return () => { live = false }
  }, [cycleId])

  const yearEnd = pop?.review_type === 'yearend'
  return (
    <Dialog open={cycleId != null} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{pop ? `${pop.name}: who's counted` : "Who's counted"}</DialogTitle>
        </DialogHeader>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!pop && !error && (
          <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Working it out…</p>
        )}
        {pop && (
          <div className="space-y-4 text-sm">
            <p className="text-gray-700">
              <span className="font-semibold">{pop.counts.counted}</span> people are counted in this cycle&apos;s check-ins
              and progress reports. Check-ins end {day(pop.checkin_end_date)}.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-gray-600">
              {pop.counts.no_longer_employed != null && (
                <li>
                  <span className="font-medium">{pop.counts.no_longer_employed}</span> left out because they&apos;re no
                  longer employed, though their account is still active.
                </li>
              )}
              {yearEnd && (
                <li><span className="font-medium">{pop.counts.ring_fenced}</span> left out by the year-end ring-fence (their title).</li>
              )}
              <li>
                <span className="font-medium">{pop.counts.too_soon_to_rate}</span> Too Soon To Rate: hired after {day(pop.tstr_line)}.
                They still have their check-in, rated Too Soon To Rate, and aren&apos;t in the progress reports.
              </li>
              <li>
                <span className="font-medium">{pop.counts.out_of_cycle}</span> not in the cycle at all (ICA, Casual,
                Learnership, or excluded in Admin &gt; Users).
              </li>
            </ul>
            {yearEnd && pop.ring_fenced.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="font-semibold text-gray-900">Left out by the ring-fence</h4>
                <PeopleTable people={pop.ring_fenced} last="reason" />
              </div>
            )}
            {pop.too_soon_to_rate.length > 0 && (
              <div className="space-y-1.5">
                <h4 className="font-semibold text-gray-900">Too Soon To Rate</h4>
                <PeopleTable people={pop.too_soon_to_rate} last="first_hire" />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => download(pop)}>
                <Download className="mr-2 h-4 w-4" /> Download the list (CSV)
              </Button>
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CyclePopulationDialog
